import { sdk } from "@/lib/api/client.js";
import { useReferenceStore } from "@/store/reference.store.js";
import { Logger } from "@/lib/app-log.js";
import {
  usesBulkOrLazyHydration,
  usesHybridSeedEndpoint,
} from "@/lib/reference-dropdown-source.js";

const MAX_SEED_LIMIT = 1000;

function shouldLogReferenceDropdownFallback() {
  return (
    (typeof import.meta !== "undefined" && Boolean(import.meta.env?.DEV)) ||
    (typeof import.meta !== "undefined" &&
      import.meta.env?.VITE_LOG_REFERENCE_DROPDOWN_FALLBACK === "true")
  );
}

function rowsFromCache(entityKey) {
  const raw = getHydratedReferenceRows(entityKey);
  if (raw.length > 0) {
    return { success: true, data: normalizeReferenceDropdownRows(raw) };
  }
  return null;
}

/**
 * Normalize reference-data rows (formatItems adds value/label) for select/options UIs.
 */
export function normalizeReferenceDropdownRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((item) => ({
    ...item,
    value: item.value?._id ?? item.value ?? item._id,
    label:
      (typeof item.label === "string" && item.label.trim()) ||
      item.name ||
      String(item.value ?? item._id ?? ""),
  }));
}

/**
 * Rows already loaded by HydrationManager (`hydrateAll` → entities, `hydrateSeeds` → seedData).
 * Strategy-agnostic: BULK fills `entities[entityKey]`, HYBRID fills `seedData[entityKey]`.
 * Does not perform network I/O — call after `hydrationManager.hydrateAll()` when needed.
 *
 * For legacy dropdown recovery (hybrid seed API + REST fallback), use fetchDropdownOptionsFromReference.
 *
 * @param {string} entityKey — reference registry key
 * @returns {unknown[]} raw rows (may be empty)
 */
export function getHydratedReferenceRows(entityKey) {
  const ref = useReferenceStore.getState();
  let raw = ref.entities?.[entityKey];
  if (!Array.isArray(raw) || raw.length === 0) {
    raw = ref.seedData?.[entityKey];
  }
  return Array.isArray(raw) ? raw : [];
}

async function fillBulkFromReferenceCache(entityKey) {
  const { hydrationManager } = await import("@/lib/services/reference-hydration.js");
  const store = useReferenceStore.getState();
  if (store.isHydrating) {
    await hydrationManager.hydrateAll();
  }
  const afterHydrate = rowsFromCache(entityKey);
  if (afterHydrate) return afterHydrate;

  await hydrationManager.ensureEntity(entityKey);
  return rowsFromCache(entityKey);
}

/**
 * Prefer global reference cache (bulk hydrate → entities, hybrid seed → seedData),
 * then legacy dropdown API. Bulk/lazy entities never call `/reference-data/seed`.
 *
 * @param {string} entityKey — must match reference-data REFERENCE_REGISTRY / REFERENCE_CONFIG keys
 * @param {() => Promise<{ success?: boolean, data?: unknown[] }>} fallbackFn — typically SDK getDropdownParams / getDropdown
 * @param {{ preferNetwork?: boolean }} [options] — bypass shared hydration cache after tenant changes
 */
export async function fetchDropdownOptionsFromReference(entityKey, fallbackFn, options = {}) {
  if (options.preferNetwork && typeof fallbackFn === "function") {
    return await fallbackFn();
  }

  const cached = rowsFromCache(entityKey);
  if (cached) return cached;

  if (usesBulkOrLazyHydration(entityKey)) {
    try {
      const fromGetAll = await fillBulkFromReferenceCache(entityKey);
      if (fromGetAll) return fromGetAll;
      if (useReferenceStore.getState().hydratedEntities?.[entityKey]) {
        return { success: true, data: [] };
      }
    } catch {
      /* fall through to legacy dropdown */
    }
  } else if (usesHybridSeedEndpoint(entityKey)) {
    try {
      const res = await sdk.referenceData.seed({
        entity: entityKey,
        limit: MAX_SEED_LIMIT,
      });
      const rows = res?.data;
      const version = res?.version;
      if (Array.isArray(rows) && rows.length > 0) {
        useReferenceStore.getState().setSeedData(entityKey, rows, version);
        return { success: true, data: normalizeReferenceDropdownRows(rows) };
      }
    } catch {
      /* fall through */
    }
  }

  if (typeof fallbackFn === "function") {
    if (shouldLogReferenceDropdownFallback()) {
      Logger.warn("[ReferenceDropdown] using legacy dropdown/params fallback", {
        entityKey,
      });
    }
    return await fallbackFn();
  }

  return { success: true, data: [] };
}
